import { useEffect, useRef, useState } from "react";
import {
  CorrectionContextSchema,
  CorrectionResultSchema,
  UndoCorrectionInputSchema,
  VisitCorrectionInputSchema,
  type CorrectionContext,
  type UndoCorrectionInput,
  type VisitCorrectionInput,
} from "@smrti/types";

type Operation = {
  visitId: string;
  correctionId?: string;
  input: VisitCorrectionInput | UndoCorrectionInput;
};
const key = (userId: string) => `smrti:visit-place:${userId}`;
function restore(userId: string): Operation | null {
  const value = JSON.parse(sessionStorage.getItem(key(userId)) ?? "null");
  if (!value) return null;
  if (typeof value.visitId !== "string" || !value.visitId) throw new Error("Invalid saved change");
  if (value.correctionId !== undefined) {
    if (typeof value.correctionId !== "string" || !value.correctionId)
      throw new Error("Invalid saved undo");
    return {
      visitId: value.visitId,
      correctionId: value.correctionId,
      input: UndoCorrectionInputSchema.parse(value.input),
    };
  }
  const input = VisitCorrectionInputSchema.parse(value.input);
  if (input.scope !== "visit" || input.target.kind !== "existing")
    throw new Error("Invalid saved change");
  return { visitId: value.visitId, input };
}
export function recoveredVisit(userId: string): string | null {
  try {
    return restore(userId)?.visitId ?? null;
  } catch {
    return "unavailable-recovery";
  }
}

export function VisitPlaceEditor({
  userId,
  apiBase,
  visitId,
  onClose,
  onChanged,
  onSignOut,
}: {
  userId: string;
  apiBase: string;
  visitId: string;
  onClose: () => void;
  onChanged: () => void;
  onSignOut?: () => void;
}) {
  const [context, setContext] = useState<CorrectionContext | null>(null);
  const [query, setQuery] = useState("");
  const [choice, setChoice] = useState("");
  const [undo, setUndo] = useState("");
  const [preview, setPreview] = useState(false);
  const [pending, setPending] = useState<Operation | null>(null);
  const [busy, setBusy] = useState(false);
  const [blocked, setBlocked] = useState(false);
  const [message, setMessage] = useState("");
  const active = useRef(false);
  const heading = useRef<HTMLHeadingElement>(null);
  const previewHeading = useRef<HTMLHeadingElement>(null);
  const search = useRef<HTMLInputElement>(null);
  const sheet = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    // Focus conveys the new phase without a second, duplicative live announcement.
    if (preview) previewHeading.current?.focus();
    else search.current?.focus();
  }, [preview]);

  async function identity() {
    const response = await fetch(`${apiBase}/api/me`, {
      credentials: "include",
      cache: "no-store",
    });
    const body = await response.json();
    if (!response.ok || !body.ok || body.data?.userId !== userId)
      throw new Error("Sign in to the original account before continuing.");
    if (!active.current) throw new Error("Screen closed");
  }
  async function load() {
    setBusy(true);
    setContext(null);
    setChoice("");
    setUndo("");
    setPreview(false);
    try {
      await identity();
      const response = await fetch(
        `${apiBase}/api/location/visits/${encodeURIComponent(visitId)}/correction-context`,
        { credentials: "include" }
      );
      const body = await response.json();
      if (!response.ok || !body.ok) throw new Error("Could not load place choices. Try reloading.");
      const next = CorrectionContextSchema.parse(body.data);
      if (next.visitId !== visitId) throw new Error("Visit changed. Reload its details.");
      if (active.current) setContext(next);
    } catch (error) {
      if (active.current)
        setMessage(error instanceof Error ? error.message : "Could not load choices.");
    } finally {
      if (active.current) setBusy(false);
    }
  }
  useEffect(() => {
    active.current = true;
    const dialog = sheet.current;
    dialog?.showModal?.();
    heading.current?.focus();
    try {
      const operation = restore(userId);
      if (sessionStorage.getItem(`smrti:place-confirmation:${userId}`)) {
        setBlocked(true);
        setMessage("Resolve the pending Home or Work confirmation before changing this visit.");
      } else if (operation) {
        setPending(operation);
        setMessage(
          "A place change is unconfirmed. Retry the exact saved request before choosing again. Keep this tab open until resolved."
        );
      } else void load();
    } catch {
      setBlocked(true);
      setMessage("Saved change recovery is unavailable. No new change will be sent.");
    }
    return () => {
      active.current = false;
      dialog?.close?.();
    };
    // This editor is keyed by account and visit by its caller.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function send(operation: Operation) {
    setBusy(true);
    setMessage("");
    try {
      await identity();
      // Persist before the first write; storage failure must prevent submission.
      const saved = restore(userId);
      if (saved && JSON.stringify(saved) !== JSON.stringify(operation))
        throw new Error("Resolve the saved change first.");
      sessionStorage.setItem(key(userId), JSON.stringify(operation));
      setPending(operation);
      const path = operation.correctionId
        ? `corrections/${encodeURIComponent(operation.correctionId)}/undo`
        : `visits/${encodeURIComponent(operation.visitId)}/correct`;
      const response = await fetch(`${apiBase}/api/location/${path}`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(operation.input),
      });
      const body = await response.json();
      if (!active.current) return;
      await identity();
      // Only an application 409, returned after replay lookup, settles a lost-response retry.
      if (
        (response.status === 409 || (!saved && [400, 401, 403, 404].includes(response.status))) &&
        body.ok === false &&
        typeof body.error === "string"
      ) {
        sessionStorage.removeItem(key(userId));
        setPending(null);
        setMessage(
          "The change was rejected because its context changed. Review refreshed choices before deciding again."
        );
        await load();
        return;
      }
      if (!response.ok || !body.ok) throw new Error("Unconfirmed");
      const result = CorrectionResultSchema.parse(body.data);
      if (
        result.visitId !== operation.visitId ||
        result.scope !== "visit" ||
        result.undoneCorrectionId !== (operation.correctionId ?? null)
      )
        throw new Error("Unexpected result");
      sessionStorage.removeItem(key(userId));
      setPending(null);
      setMessage(
        result.retainedPlaceId
          ? "Undo saved. The personal place remains in saved places."
          : "Place change saved. Times were not confirmed or changed."
      );
      onChanged();
      setQuery("");
      await load(); // A refresh failure must never resend the confirmed write.
    } catch {
      if (active.current)
        setMessage(
          "Could not confirm the change. Check your session and retry. Saved requests are retried unchanged; do not close this tab while one is pending."
        );
    } finally {
      if (active.current) setBusy(false);
    }
  }

  const target = context?.places.find((p) => p.id === choice);
  const history = context?.corrections.find(
    (c) => c.id === undo && c.scope === "visit" && c.canUndo
  );
  function identityDetail(id: string | null, name: string | null, address: string | null) {
    if (!id) return "Unassigned";
    const place = context?.places.find((p) => p.id === id);
    const collision = context?.places.some(
      (p) => p.id !== id && p.name === name && p.formattedAddress === address
    );
    const coordinates = place
      ? `${place.latitude.toFixed(4)}, ${place.longitude.toFixed(4)}`
      : null;
    return [
      address,
      (!address || collision) && coordinates,
      (collision || !place) && `Saved place ${id}`,
    ]
      .filter(Boolean)
      .join(" · ");
  }
  const matches =
    context?.places.filter(
      (p) =>
        p.id !== context.placeId &&
        `${p.name ?? ""} ${p.formattedAddress ?? ""}`
          .toLowerCase()
          .includes(query.trim().toLowerCase())
    ) ?? [];
  const latestUndo = context?.corrections.find((item) => item.scope === "visit" && item.canUndo);
  return (
    <dialog
      ref={sheet}
      className="place-sheet"
      aria-label="Edit visit place"
      onCancel={(event) => {
        event.preventDefault();
        if (!busy && !pending) onClose();
      }}
    >
      <section className="max-w-2xl mx-auto space-y-4" aria-label="Change visit place">
        <button disabled={busy || !!pending} onClick={onClose} className="sheet-close">
          Close <span aria-hidden="true">×</span>
        </button>
        <p className="text-xs text-stone-600">
          Closing discards unsaved choices, not saved changes.
        </p>
        <h2 ref={heading} tabIndex={-1} className="text-2xl font-semibold">
          Change this visit’s place
        </h2>
        <p className="text-sm text-stone-600">
          Only this visit changes. Saved place names and other visits stay unchanged. This does not
          confirm exact times or whether you were inside rather than nearby.
        </p>
        {message && (
          <p role="status" className="rounded border border-stone-300 p-3 text-sm">
            {message}
          </p>
        )}
        {pending ? (
          <button
            disabled={busy}
            onClick={() => void send(pending)}
            className="rounded bg-teal-800 text-white px-4"
          >
            Retry same change
          </button>
        ) : (
          <>
            {!blocked && (
              <button disabled={busy} onClick={() => void load()} className="underline">
                Reload choices
              </button>
            )}
            {context && !blocked && (
              <>
                <p>
                  Current place:{" "}
                  <strong>{context.placeName ?? "Unassigned or unnamed place"}</strong>
                </p>
                {!preview && latestUndo && (
                  <button
                    disabled={busy}
                    className="underline"
                    onClick={() => {
                      setUndo(latestUndo.id);
                      setChoice("");
                      setPreview(true);
                    }}
                  >
                    Undo last place change
                  </button>
                )}
                {preview ? (
                  <div className="rounded border border-stone-300 bg-white p-4 space-y-3">
                    <h3 ref={previewHeading} tabIndex={-1} className="font-semibold">
                      {history ? "Undo this visit’s assignment?" : "Move only this visit?"}
                    </h3>
                    <p>
                      {history
                        ? `${history.afterName ?? "Unnamed place"} → ${history.beforePlaceId === null ? "Unassigned" : (history.beforeName ?? "Unnamed saved place")}`
                        : `${context.placeName ?? "Unassigned or unnamed place"} → ${target?.name ?? "Unnamed saved place"}`}
                    </p>
                    <p className="text-sm break-words">
                      {history
                        ? identityDetail(
                            history.beforePlaceId,
                            history.beforeName,
                            history.beforeAddress
                          )
                        : target && identityDetail(target.id, target.name, target.formattedAddress)}
                    </p>
                    <p className="text-sm">
                      One visit. No saved-place rename. No timing confirmation.
                    </p>
                    <div className="flex gap-4">
                      <button
                        disabled={busy || (!target && !history)}
                        className="rounded bg-teal-800 text-white px-4"
                        onClick={() => {
                          const fields = {
                            operationId: crypto.randomUUID(),
                            contextToken: context.contextToken,
                          };
                          void send(
                            history
                              ? { visitId, correctionId: history.id, input: fields }
                              : {
                                  visitId,
                                  input: {
                                    ...fields,
                                    scope: "visit",
                                    target: { kind: "existing", placeId: target!.id },
                                  },
                                }
                          );
                        }}
                      >
                        {history ? "Confirm undo" : "Save place for this visit"}
                      </button>
                      <button disabled={busy} onClick={() => setPreview(false)}>
                        Back to choices
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <label className="block">
                      Search saved places
                      <input
                        ref={search}
                        type="search"
                        value={query}
                        disabled={busy}
                        onChange={(e) => {
                          setQuery(e.target.value);
                          setChoice("");
                        }}
                        className="mt-1 block w-full rounded border border-stone-300 bg-white p-2"
                      />
                    </label>
                    <fieldset disabled={busy} className="space-y-2">
                      <legend className="text-sm text-stone-600">Choose a saved place</legend>
                      {matches.map((p) => (
                        <label
                          key={p.id}
                          className="flex items-start gap-3 rounded border border-stone-200 bg-white p-3"
                        >
                          <input
                            type="radio"
                            name="saved-place"
                            checked={choice === p.id}
                            onChange={() => {
                              setChoice(p.id);
                              setUndo("");
                            }}
                            className="mt-1"
                          />
                          <span>
                            {p.name ?? "Unnamed saved place"}
                            <span className="block text-sm text-stone-600 break-words">
                              {identityDetail(p.id, p.name, p.formattedAddress)}
                            </span>
                          </span>
                        </label>
                      ))}
                    </fieldset>
                    {!matches.length && (
                      <p className="text-sm">
                        No matching saved places. This search does not look up new places.
                      </p>
                    )}
                    <button
                      disabled={busy || !target}
                      onClick={() => {
                        setUndo("");
                        setPreview(true);
                      }}
                      className="rounded bg-teal-800 text-white px-4"
                    >
                      Review change
                    </button>
                    <details>
                      <summary>Assignment history</summary>
                      {context.corrections
                        .filter((c) => c.scope === "visit")
                        .map((c) => (
                          <div key={c.id} className="border-t py-3 text-sm">
                            <p>
                              {c.beforeName ?? "Previous assignment"} →{" "}
                              {c.afterName ?? "Unnamed place"}
                            </p>
                            <p>{new Date(c.createdAt).toLocaleString()}</p>
                            {c.canUndo ? (
                              <button
                                disabled={busy}
                                onClick={() => {
                                  setUndo(c.id);
                                  setChoice("");
                                  setPreview(true);
                                }}
                                className="underline"
                              >
                                Review undo
                              </button>
                            ) : (
                              <p>Undo unavailable: already undone or superseded.</p>
                            )}
                          </div>
                        ))}
                    </details>
                  </>
                )}
              </>
            )}
          </>
        )}
        {onSignOut && (busy || pending || blocked) && (
          <button onClick={onSignOut} className="block underline">
            Sign out (keep saved change)
          </button>
        )}
      </section>
    </dialog>
  );
}
